import React from 'react'
import { Box, Typography, Container } from '@mui/material'

export default function PrivacyPolicy() {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				py: { xs: 4, md: 8 },
				px: 2
			}}
		>
			<Container maxWidth="md">
				<Box sx={{ maxWidth: 800, mx: 'auto' }}>
					<Typography
						variant="h1"
						component="h1"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 700,
							fontSize: { xs: '28px', md: '40px' },
							color: '#2D3748',
							mb: 2
						}}
					>
						Nami Privacy Policy
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							fontSize: { xs: '14px', md: '16px' },
							color: '#718096',
							mb: 4
						}}
					>
						Last updated: January 11th, 2026
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						This Privacy Policy explains how Nami ("Nami," "we," "us," "our") collects, uses, and shares information when you use our software and related websites/pages (the "Service").
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 4
						}}
					>
						If you do not agree with this Policy, do not use the Service.
					</Typography>

					{/* Section 1 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						1) Who this applies to
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						Nami is a software platform used by pet care businesses ("Businesses") and their authorized staff to manage appointments, clients/pets, services, staff, reminders, and online booking. Customers of those Businesses ("Customers") may interact with booking pages and communications enabled by a Business.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						<strong>Important:</strong> If you are a Customer of a Business using Nami, that Business may control and use your information for its own purposes (for example, scheduling and communicating with you). This Privacy Policy covers Nami's handling of information in connection with the Service, but it does not cover a Business's own privacy practices. Please contact the Business directly for questions about how it uses your information.
					</Typography>

					{/* Section 2 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						2) Information we collect
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We may collect the following categories of information, depending on how you use the Service:
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						A. Information you provide
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 2, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Account and profile information (e.g., name, email, phone number, business name, and other information you submit when creating or managing an account)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Scheduling and service data (e.g., appointment details, services selected, notes you enter)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Client and pet information entered by a Business (e.g., customer name and contact details, pet name and basic details, notes added by the Business)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Communications (e.g., messages you send to us; limited information needed to send reminders or booking communications)
						</Typography>
					</Box>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						B. Information collected automatically
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 3, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Usage and device data (e.g., IP address, browser type, pages/screens viewed, approximate location inferred from IP, and similar log data)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Cookies and similar technologies (see Section 7)
						</Typography>
					</Box>

					{/* Section 3 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						3) How we use information
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We use information to:
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 3, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Provide and operate the Service (including creating accounts, managing bookings, reminders, and online booking flows)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Communicate with you about the Service (for example, support responses, administrative notices)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Maintain security and prevent misuse (e.g., troubleshooting, monitoring for suspicious activity)
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							Comply with legal obligations and enforce our terms/policies
						</Typography>
					</Box>

					{/* Section 4 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						4) How we share information
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We may share information in the following situations:
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						A. With the Business you're booking with
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						If you are a Customer interacting with a Business through Nami, information you submit (e.g. booking details and contact info) is shared with that Business so it can provide services and communicate with you.
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						B. With service providers
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We may use vendors to help operate the Service (for example, hosting, analytics, email delivery, customer support tools, and text messaging providers). These vendors process information only to perform services for us.
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						C. Legal and safety
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 1
						}}
					>
						We may disclose information if we reasonably believe it is necessary to:
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 2, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							comply with law, regulation, or a valid legal process, or
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							protect the rights, safety, or security of users, the public, or Nami.
						</Typography>
					</Box>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						D. Business transfers
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						If we're involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets, information may be transferred as part of that transaction.
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						E. With your direction or consent
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We may share information when you ask us to or consent.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						Sale of data: We do not sell personal information.
					</Typography>

					{/* Section 5 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						5) SMS and Mobile Messaging
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We collect mobile phone numbers when customers voluntarily provide them to receive text messages related to appointments and services. Message frequency may vary. Message and data rates may apply. Customers may opt out at any time by replying STOP or request help by replying HELP.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						We do not share mobile phone numbers or SMS opt-in data with third parties or affiliates for marketing or promotional purposes. Mobile information is shared only with service providers necessary to deliver messaging services. Text messaging originator opt-in data and consent are not shared with any third parties.
					</Typography>

					{/* Section 6 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						6) Your choices and privacy rights
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						Depending on where you live, you may have rights to:
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 2, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							request access to the personal information we hold about you,
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							request correction of inaccurate information,
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							request deletion of certain information, and/or
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							receive a copy of certain information.
						</Typography>
					</Box>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						To make a request, contact us at admin@nami.pet. We may need to verify your identity before completing a request. In some cases, we may deny a request where permitted by law.
					</Typography>

					{/* Section 7 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						7) Cookies and similar technologies
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We may use cookies and similar tools to help the Service work, understand usage, and improve performance.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						You can control cookies through your browser settings. If you disable certain cookies, some parts of the Service may not function properly.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						<em>Analytics (optional; delete if not applicable):</em> We may use analytics providers (e.g., Google Analytics) to understand how the Service is used.
					</Typography>

					{/* Section 8 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						8) Links to third-party sites
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						The Service may contain links to third-party sites. We are not responsible for the privacy practices of those third parties. Please review their policies.
					</Typography>

					{/* Section 9 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						9) Data retention
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We keep personal information for as long as reasonably necessary to:
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 3, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							provide the Service,
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							meet legal or compliance obligations,
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							resolve disputes, and
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							enforce agreements.
						</Typography>
					</Box>

					{/* Section 10 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						10) Security
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						We use reasonable administrative, technical, and organizational measures intended to protect personal information. No system is 100% secure, and you use the Service at your own risk.
					</Typography>

					{/* Section 11 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						11) Minors
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						The Service is intended for businesses and their customers and is not designed for individuals under the age of eighteen (18). If you believe a child has provided personal information to Nami, contact us at admin@nami.pet.
					</Typography>

					{/* Section 12 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						12) Contact us
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 1
						}}
					>
						Questions or requests about privacy:
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568'
						}}
					>
						Email: admin@nami.pet
					</Typography>
				</Box>
			</Container>
		</Box>
	)
}
